import { Patch } from 'immer';
import clonedeep from 'lodash.clonedeep';
import isEqual from 'lodash.isequal';
import { PathSegmentName, UnsavedPatch } from 'modules/Editor/store/history/history.types';
import { WidgetId } from 'types/idTypes';

import { InfographRow, PageRow, WidgetRow } from './types';

type CombinedUnsavedPatchObject = {
  [key: string]: {
    patches: Patch[];
  };
};

/**
 * Combine the unsaved patches to map object
 * And if the same path has multiple patches, use the latest patch
 *
 * e.g. [{ path: '/infographs/1/pages/2/widgets/3', patch: { op: 'replace', path: '/infographs/1/pages/2/widgets/3', value: 'test' } }]
 * => { '/infographs/1/pages/2/widgets/3': { patches: [{ op: 'replace', path: '/infographs/1/pages/2/widgets/3', value: 'test' }] } }
 * @param unsavedPatches
 * @returns
 */
const combineUnsavedPatchesToMap = (unsavedPatches: UnsavedPatch[]): CombinedUnsavedPatchObject => {
  const result = unsavedPatches.reduce(
    (prev: CombinedUnsavedPatchObject, cur: UnsavedPatch): CombinedUnsavedPatchObject => {
      if (!prev[cur.path]) {
        // init path
        prev[cur.path] = {
          patches: [cur.patch],
        };
      } else {
        // Filter the same path to use the latest patch
        prev[cur.path].patches = prev[cur.path].patches.filter(
          (patch: Patch) => !(patch.op === cur.patch.op && isEqual(patch.path, cur.patch.path)),
        );
        prev[cur.path].patches.push(cur.patch);
      }

      return prev;
    },
    {},
  );

  return result;
};

/**
 * Parse the path to map object
 * e.g. /infographs/1/pages/2/widgets/3 => { type: 'widgets', id: '3' }
 * @param path
 * @returns
 */
const parsePathToMap = (
  path: string,
): {
  type: PathSegmentName;
  id: string;
} => {
  const pathArray = path.split('/');
  const [infographKey, infographId, pageKey, pageId, widgetKey, widgetId] = pathArray;

  // Check the key is valid
  if (pathArray.length % 2 !== 0 || pathArray.length > 6 || pathArray.length <= 0) throw new Error('Invalid path');
  if (
    (pathArray.length >= 2 && infographKey !== 'infographs') ||
    (pathArray.length >= 4 && pageKey !== 'pages') ||
    (pathArray.length >= 6 && widgetKey !== 'widgets')
  ) {
    throw new Error('Invalid path');
  }

  const type = (widgetKey ?? pageKey ?? infographKey) as PathSegmentName;
  const id = type === widgetKey ? widgetId : type === pageKey ? pageId : infographId;
  return { type, id };
};

/**
 * Parse the apply patches for using it in the backend
 * if it is the widgets or pages type, remove the first two path segments.
 * else return the original patches for infographs
 *
 * @param patches
 * @param type
 * @returns
 */
const parseApplyPatches = (patches: Patch[], type: PathSegmentName): Patch[] => {
  const result = ['widgets', 'pages'].includes(type)
    ? patches.map((patch: Patch) => {
        const newPatch: Patch = clonedeep(patch); // Generate the new cloned applyPatch
        newPatch.path = newPatch.path.slice(2); // Parse the path for using in the backend
        return newPatch;
      }) // Remove the first two path segements from the applyPatch
    : patches;

  return result;
};

/**
 * Parse checksum from row data
 * @param rowData
 * @returns {{ [key: WidgetId]: string }}
 */
const parseChecksum = (rowData: InfographRow | PageRow | WidgetRow): { [key: WidgetId]: string | undefined } => {
  if (!rowData?.id || !rowData?.tags?.checksum) return {};
  return { [rowData.id]: rowData?.tags?.checksum };
};

/**
 * Parse checksum list from row[] data
 * @param rowDataList
 * @returns {{ [key: WidgetId]: string }}
 */
const parseChecksumList = (
  rowDataList: InfographRow[] | PageRow[] | WidgetRow[],
): {
  [key: WidgetId]: string | undefined;
} => {
  return rowDataList
    .map((data) => ({ id: data.id, checksum: data?.tags?.checksum }))
    .reduce<{ [key: WidgetId]: string }>((prev, cur) => {
      if (!cur?.id || !cur?.checksum) return prev;
      prev[cur.id] = cur.checksum;
      return prev;
    }, {});
};

/**
 * Returns true if should use the 'delete' command instead of a patch
 * Delete patches look like
 * { op: 'remove', path: [] }
 *
 * Need to check the last patch only for the case where a user performs rapid undo/redo within the same commit
 * For example, the following would be a valid set of patches for a delete.
 * [
 *  { op: 'replace', path: [ 'pages', '1', 'widgets', '1', 'data', 'title' ], value: 'test' },
 *  { op: 'replace', path: [ 'pages', '1', 'widgets', '1', 'data', 'title' ], value: 'test2' },
 *  { op: 'remove', path: [] }
 * ];
 *
 * The following would not be considered a deletion:
 * [
 *  { op: 'remove', path: [] },
 *  { op: 'add', path: [], value: widget } // op: 'add' means the widget is being added back
 * ]
 *
 * @param patch
 * @returns boolean - true if the patch is a delete op
 */
const checkIsDeleteOp = (patches: Patch[]): boolean => {
  if (!patches.length) return false;

  const lastPatch = patches[patches.length - 1];
  return lastPatch.op === 'remove' && lastPatch.path.length === 0;
};

export {
  combineUnsavedPatchesToMap,
  parsePathToMap,
  parseApplyPatches,
  parseChecksum,
  parseChecksumList,
  checkIsDeleteOp,
};
